import { Box } from "@mui/material";
import TextField from "@mui/material/TextField";
import { AutomationMethods } from "enums/Methods";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import { SelectedSystem } from "features/WorklistTools/shared/components/SelectedSystem";
import {
  PlatePosition,
  SeedingStrategy,
} from "features/WorklistTools/shared/interfaces";
import {
  gForceToRpm,
  rpmToGForce,
} from "features/WorklistTools/shared/WorklistHelpers";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import styled from "styled-components";
import { ExternalThawToolActions } from "../../state";
import { useHandleMethodSettingsUpdated } from "../../state/handlers";
import { useExternalThawStateProvider } from "../../state/StateProvider";
import { AliquotSettings } from "./components/AliquotSettings";
import {
  DefaultVSpinSettings,
  DefaultVSpinSettingsDict,
} from "./components/DefaultVSpinSettings";
import { PelletResuspensionVolume } from "./components/PelletResuspensionVolume";
import { SeedingStrategyToggle } from "./components/SeedingStrategyToggle";
import { SplitEvenlyCheckBox } from "./components/SplitEvenlyCheckBox";

const StyledMethodSettingsContainer = styled.div`
  height: 500px;
  overflow: auto;
`;

const StyledMethodSettingsContentContainer = styled.div`
  display: grid;
  grid-template: auto / 1fr;
  grid-gap: 15px;
  margin-top: 5px;
  margin-right: 15px;
`;

export function MethodSettings({ deadPlateTypeInfo }: any) {
  const { externalThawInternalState } = useExternalThawStateProvider();
  const externalThawState = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present
  );
  const methodSettings = externalThawInternalState.methodSettings;
  const dispatch = useAppReduxDispatch();
  const updateMethodSettings = useHandleMethodSettingsUpdated();

  const handleMethodSettingsChanged = async (key: string, value: any) => {
    switch (key) {
      case "spinParamGForce":
        const spinRPM = gForceToRpm(value);
        updateMethodSettings("spinParamPercent", spinRPM);

        break;
      case "spinParamPercent":
        const GForceValue = rpmToGForce(value);
        updateMethodSettings("spinParamGForce", GForceValue);
        break;
      case "defaultVSpinSettings":
        handleDefaultVSpinSettings(value);
        break;
    }
    updateMethodSettings(key, value);
  };

  const handleDefaultVSpinSettings = (value: boolean) => {
    if (value) {
      for (const [key, value] of DefaultVSpinSettingsDict) {
        updateMethodSettings(key, value);
      }
    } else {
      for (const [key] of DefaultVSpinSettingsDict) {
        updateMethodSettings(key, 0);
      }
    }
  };

  const handleDeadPlateBarcodeChange = (value: string) => {
    dispatch(
      ExternalThawToolActions.UPDATE_DEAD_PLATEBARCODE({
        value,
      })
    );
  };

  const handleDeadPlateTypeChange = (value: string) => {
    dispatch(
      ExternalThawToolActions.UPDATE_DEAD_PLATETYPE({
        value,
      })
    );
  };

  const handleIntPlateInfoChanged = (
    index: number,
    key: string,
    value: any
  ) => {
    dispatch(
      ExternalThawToolActions.UPDATE_INT_PLATE_INFO({
        index,
        key,
        value,
      })
    );
  };

  return (
    <StyledMethodSettingsContainer>
      <StyledMethodSettingsContentContainer>
        <SelectedSystem
          automationMethodId={AutomationMethods.ExternalThaw}
          selectedSystem={methodSettings.selectedSystem}
          handleSystemChanged={({ systemId, asamId }) => {
            updateMethodSettings("arraySystemAutomationMethodId", asamId);
            updateMethodSettings("selectedSystem", systemId);
          }}
        />
        <PelletResuspensionVolume
          item={externalThawState.intPlateInfo[0]}
          index={0}
          handleIntPlateInfoChanged={handleIntPlateInfoChanged}
          width={100}
        />
        <Box>
          <SeedingStrategyToggle />
          {methodSettings.seedingStrategy === SeedingStrategy.Volumetric ? (
            <SplitEvenlyCheckBox />
          ) : null}
        </Box>
        <TextField
          size="small"
          value={externalThawState.deadPlateBarcode}
          label="Dead PlateBarcode"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleDeadPlateBarcodeChange(e.target.value.toString())
          }
          disabled={true}
        />
        <PlateType
          platePosition={PlatePosition.Dead}
          isDisabled={methodSettings!.selectedSystem === 0}
          acceptedPlateTypes={deadPlateTypeInfo}
          plateType={externalThawState.deadPlateType}
          setPlateType={(plateTypeInfo) =>
            handleDeadPlateTypeChange(plateTypeInfo.labwareTypeCode)
          }
        />

        <AliquotSettings
          numberOfAliquots={methodSettings.numberOfAliquots}
          aliquotVolume={methodSettings.aliquotVolume}
          incubationTime={methodSettings.incubationTime}
          handleMethodSettingsChanged={handleMethodSettingsChanged}
        />
        <TextField
          size="small"
          value={methodSettings.experimentSettings}
          label="Experiment Settings"
          disabled={true}
        />
        <DefaultVSpinSettings
          externalThawInternalState={externalThawInternalState}
          handleMethodSettingsChanged={handleMethodSettingsChanged}
        />
        <NumberField
          size={"small"}
          id="spin-gforce"
          label="Spin Params G Force (RCF)"
          value={methodSettings.spinParamGForce}
          onChange={(value) =>
            handleMethodSettingsChanged("spinParamGForce", parseInt(value))
          }
          disabled={methodSettings.defaultVSpinSettings}
        />
        <NumberField
          size={"small"}
          id="spin-percentage"
          label="Spin Params (%)"
          value={methodSettings.spinParamPercent}
          onChange={(value) =>
            handleMethodSettingsChanged("spinParamPercent", parseInt(value))
          }
          disabled={methodSettings.defaultVSpinSettings}
          minValue={5}
          maxValue={100}
        />
        <NumberField
          size={"small"}
          id="spin-time"
          label="Spin Time (Minutes)"
          value={methodSettings.spinTime}
          onChange={(value) =>
            handleMethodSettingsChanged("spinTime", parseInt(value))
          }
          disabled={methodSettings.defaultVSpinSettings}
          minValue={1}
          maxValue={10}
        />
        <NumberField
          size={"small"}
          id="spin-acceleration"
          label="Acceleration Settings (%)"
          value={methodSettings.spinAccel}
          onChange={(value) =>
            handleMethodSettingsChanged("spinAccel", parseInt(value))
          }
          disabled={methodSettings.defaultVSpinSettings}
          minValue={5}
          maxValue={100}
        />
        <NumberField
          size={"small"}
          id="spin-deceleration"
          label="Deceleration Settings (%)"
          value={methodSettings.spinDecel}
          onChange={(value) =>
            handleMethodSettingsChanged("spinDecel", parseInt(value))
          }
          disabled={methodSettings.defaultVSpinSettings}
          minValue={5}
          maxValue={100}
        />
      </StyledMethodSettingsContentContainer>
    </StyledMethodSettingsContainer>
  );
}
